/**
 * Message Validation Utilities
 * 
 * Features:
 * - Input content validation and sanitization
 * - Message structure validation
 * - Timestamp validation and parsing
 * - Content type checking and formatting
 * 
 * Logic:
 * - Validates message content for non-empty, meaningful input
 * - Checks message structure for required fields and proper types
 * - Validates and parses timestamps with error handling
 * - Sanitizes user input to prevent common issues
 * 
 * Changes:
 * - Extracted from Chat.tsx component for better separation of concerns
 * - Added comprehensive input validation functions
 * - Created reusable validation patterns
 * - Standardized validation error handling
 */

import { ChatMessage } from '../../models/ChatMessage';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  sanitizedValue?: string;
}

export interface MessageValidationOptions {
  minLength?: number;
  maxLength?: number;
  allowEmpty?: boolean;
  trimWhitespace?: boolean;
}

/**
 * Validates and sanitizes message content
 * @param content Message content to validate
 * @param options Validation options
 * @returns Validation result with sanitized content
 */
export const validateMessageContent = (
  content: string,
  options: MessageValidationOptions = {}
): ValidationResult => {
  const {
    minLength = 1,
    maxLength = 10000,
    allowEmpty = false,
    trimWhitespace = true
  } = options;

  if (typeof content !== 'string') {
    return {
      isValid: false,
      error: 'Content must be a string'
    };
  }

  const sanitized = trimWhitespace ? content.trim() : content;

  if (!allowEmpty && sanitized.length === 0) {
    return {
      isValid: false,
      error: 'Message content cannot be empty'
    };
  }

  if (sanitized.length < minLength) {
    return {
      isValid: false,
      error: `Message must be at least ${minLength} characters long`
    };
  }

  if (sanitized.length > maxLength) {
    return {
      isValid: false,
      error: `Message cannot exceed ${maxLength} characters`
    };
  }

  return {
    isValid: true,
    sanitizedValue: sanitized
  };
};

/**
 * Validates message structure and required fields
 * @param message Message object to validate
 * @returns True if message structure is valid
 */
export const validateMessageStructure = (message: any): message is ChatMessage => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  // Check required fields
  if (!message.role || typeof message.role !== 'string') {
    return false;
  }

  if (!message.timestamp || typeof message.timestamp !== 'string') {
    return false;
  }

  if (message.id === undefined) {
    return false;
  }

  // Validate role values
  const validRoles = ['user', 'assistant', 'tool', 'system'];
  if (!validRoles.includes(message.role)) {
    return false;
  }

  // Check content if present
  if (message.content !== undefined && typeof message.content !== 'string') {
    return false;
  }

  return true;
};

/**
 * Validates and parses timestamp
 * @param timestamp Timestamp string to validate
 * @returns Validation result with parsed date
 */
export const validateTimestamp = (timestamp: string): ValidationResult & { parsedDate?: Date } => {
  if (typeof timestamp !== 'string') {
    return {
      isValid: false,
      error: 'Timestamp must be a string'
    };
  }

  if (timestamp.trim().length === 0) {
    return {
      isValid: false,
      error: 'Timestamp cannot be empty'
    };
  }

  try {
    const parsedDate = new Date(timestamp);

    if (isNaN(parsedDate.getTime())) {
      return {
        isValid: false,
        error: 'Invalid timestamp format'
      };
    }

    return {
      isValid: true,
      parsedDate,
      sanitizedValue: parsedDate.toISOString()
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Failed to parse timestamp'
    };
  }
};

/**
 * Validates message role
 * @param role Role string to validate
 * @returns True if role is valid
 */
export const validateMessageRole = (role: string): boolean => {
  const validRoles = ['user', 'assistant', 'tool', 'system'];
  return typeof role === 'string' && validRoles.includes(role);
};

/**
 * Sanitizes user input content
 * @param content Content to sanitize
 * @returns Sanitized content
 */
export const sanitizeUserInput = (content: string): string => {
  if (typeof content !== 'string') {
    return '';
  }

  return content
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
    .replace(/[\x00-\x1F\x7F]/g, '') // Remove control characters
    .substring(0, 10000); // Limit length
};

/**
 * Validates form input for message submission
 * @param inputValue Form input value
 * @returns True if input is valid for submission
 */
export const validateFormInput = (inputValue: string): boolean => {
  const validation = validateMessageContent(inputValue);
  return validation.isValid;
};

/**
 * Checks if content appears to be meaningful (not just whitespace or repeated characters)
 * @param content Content to check
 * @returns True if content appears meaningful
 */
export const isMeaningfulContent = (content: string): boolean => {
  if (!content || typeof content !== 'string') {
    return false;
  }

  const trimmed = content.trim();

  // Check minimum length
  if (trimmed.length < 1) {
    return false;
  }

  // Check for repeated single character (e.g., "aaaa" or "....")
  if (trimmed.length > 3 && /^(.)\1+$/.test(trimmed)) {
    return false;
  }

  // Check for only punctuation or special characters
  if (/^[^\w\s]+$/.test(trimmed)) {
    return false;
  }

  return true;
};

/**
 * Validates array of messages
 * @param messages Array of messages to validate
 * @returns Validation results for each message
 */
export const validateMessages = (messages: any[]): {
  validMessages: ChatMessage[];
  invalidMessages: any[];
  errors: string[];
} => {
  const validMessages: ChatMessage[] = [];
  const invalidMessages: any[] = [];
  const errors: string[] = [];

  if (!Array.isArray(messages)) {
    return {
      validMessages: [],
      invalidMessages: [messages],
      errors: ['Input is not an array']
    };
  }

  messages.forEach((message, index) => {
    if (validateMessageStructure(message)) {
      validMessages.push(message);
    } else {
      invalidMessages.push(message);
      errors.push(`Message at index ${index} has invalid structure`);
    }
  });

  return {
    validMessages,
    invalidMessages,
    errors
  };
};
